import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { StatefulButton } from '@openedx/paragon';
import { DEFAULT_ICONS_FOR_STATEFUL_BUTTON, Form } from 'components/ui/atoms';
import {
  getIsCTADisabled,
  setIsCTADisabled,
  useAppDispatch,
  useAppSelector,
} from 'store';
import { ElementClicked, elementEvents, ElementType } from 'utils/eventing';
import { centsToDollarString } from 'utils/price';
import { couponSchema } from './coupon-schema';
import { CouponFormProps, CouponSchema } from './types';

export const CouponForm = ({
  cartDiscount,
  hasDiscount,
  onApplyCoupon,
  onRemoveCoupon,
}: CouponFormProps) => {
  const dispatch = useAppDispatch();
  const isCTADisabled = useAppSelector(getIsCTADisabled);
  const [isCTAPending, setIsCTAPending] = useState(false);

  const buttonState = isCTAPending
    ? 'pending'
    : isCTADisabled
      ? 'disabled'
      : 'default';

  const setCTAState = (value: boolean) => {
    setIsCTAPending(value);
    dispatch(setIsCTADisabled(value));
  };

  const applyCouponTagularElement: ElementClicked['webElement'] = {
    elementType: ElementType.Button,
    name: 'apply-discount-cta',
    text: 'Apply',
    htmlId: 'apply-discount-click',
    location: 'checkout',
  };

  const removeCouponTagularElement: ElementClicked['webElement'] = {
    elementType: ElementType.Button,
    name: 'remove-discount-cta',
    text: 'Remove',
    htmlId: 'remove-discount-click',
    location: 'checkout',
  };

  const { elementClicked: applyCouponElementClicked } = elementEvents({
    webElement: applyCouponTagularElement,
  });

  const { elementClicked: removeCouponElementClicked } = elementEvents({
    webElement: removeCouponTagularElement,
  });

  const handleApplyCoupon = async ({ coupon }: CouponSchema) => {
    if (coupon) {
      applyCouponElementClicked();
      setCTAState(true);

      const code = coupon;
      await onApplyCoupon(code);
      setCTAState(false);
    }
  };

  const handleRemoveCoupon = async () => {
    if (cartDiscount?.discount) {
      removeCouponElementClicked();
      setCTAState(true);
      await onRemoveCoupon(cartDiscount.discount);
      setCTAState(false);
    }
  };

  if (hasDiscount) {
    return (
      <div aria-label="applied-coupon" className="d-flex align-items-center">
        <span className="text-muted">
          {cartDiscount.discount?.type === 'absolute' ? (
            <FormattedMessage
              id="payment.coupon.benefit.absolute"
              defaultMessage="Coupon {code} applied for {discountAmount} off"
              description="A coupon has been applied for a absolute coupon."
              values={{
                code: cartDiscount?.discount?.code,
                discountAmount: centsToDollarString(
                  cartDiscount.discount?.value?.[0].centAmount,
                  cartDiscount.discount?.value?.[0].fractionDigits,
                ),
              }}
            />
          ) : (
            <FormattedMessage
              id="payment.coupon.benefit.relative"
              defaultMessage="Coupon {code} applied for {discountAmount}% off"
              description="A coupon has been applied for a percentage coupon, like 10%.  Please place the % symbol as appropriate."
              values={{
                code: cartDiscount?.discount?.code,
                discountAmount: cartDiscount?.percent,
              }}
            />
          )}
        </span>
        <StatefulButton
          id="removeCouponButton"
          className="display-inline p-0 pl-3 border-0 remove-coupon-btn-link"
          variant="link"
          onClick={handleRemoveCoupon}
          state={buttonState}
          disabledStates={['pending', 'disabled']}
          icons={{
            ...DEFAULT_ICONS_FOR_STATEFUL_BUTTON,
            pending: undefined,
          }}
          labels={{
            default: (
              <FormattedMessage
                id="payment.coupon.remove.form.submit.button"
                defaultMessage="Remove"
                description="Button for submitting the remove coupon form"
              />
            ),
          }}
        />
      </div>
    );
  }

  return (
    <Form
      aria-label="coupon-form"
      className="d-flex flex-column"
      validationSchema={couponSchema}
      defaultValues={{ coupon: '' }}
      shouldResetForm
      onSubmit={handleApplyCoupon}
    >
      <Form.Label
        htmlFor="coupon"
        className="pgn__form-label apply-coupon-label"
        style={{
          fontSize: '0.75rem',
          fontWeight: 400,
          color: '#222',
          fontFamily: 'Inter, Helvetica Neue, Arial, sans-serif',
        }}
      >
        <FormattedMessage
          id="payment.coupon.label"
          defaultMessage="Add coupon code (optional)"
          description="Label for the add coupon form"
        />
      </Form.Label>
      <div className="d-flex align-items-start">
        <Form.Input
          name="coupon"
          label=""
          autocomplete="off"
          styleClasses="mb-0 mr-2"
          type="text"
          disabled={isCTADisabled}
        />
        <StatefulButton
          id="applyCouponButton"
          variant="outline-primary"
          type="submit"
          state={buttonState}
          icons={DEFAULT_ICONS_FOR_STATEFUL_BUTTON}
          disabledStates={['pending', 'disabled']}
          labels={{
            default: (
              <FormattedMessage
                id="payment.coupon.form.submit.button"
                defaultMessage="Apply"
                description="Button for submitting the add coupon form"
              />
            ),
            pending: (
              <FormattedMessage
                id="payment.coupon.form.submit.button.loading"
                defaultMessage="Applying"
                description="Button for submitting the add coupon form when it is in the pending state"
              />
            ),
          }}
        />
      </div>
    </Form>
  );
};
